import { DestroyRef, inject, Injector, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { OnyxTime } from '@onyx/angular';
import { OnyxAddressType } from '@onyx/angular';
import { chain, clamp, mapValues, random, sample, sampleSize } from 'lodash';
import { DateTime, Duration } from 'luxon';
import type { Observable } from 'rxjs';
import {
  combineLatestWith,
  filter,
  forkJoin,
  map,
  merge,
  of,
  scan,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { v7 as uuidv7 } from 'uuid';
import { ENABLE_MOCKS } from '../../../config/config';
import { environment } from '../../../config/environment';
import { getNamespacedUrl } from '../../auth/common/interceptors/auth.interceptor';
import { AuthStore } from '../../auth/common/stores/auth.store';
import { DelegationStatus } from '../../dashboard/delegations/common/enums/delegation-status';
import type { SimplifiedDelegation } from '../../dashboard/delegations/common/interfaces/delegation';
import { FleetState } from '../../dashboard/fleet/common/enums/fleet-state';
import { FleetHelper } from '../../dashboard/fleet/common/helpers/fleet.helper';
import { FleetTypeHelper } from '../../dashboard/fleet/common/helpers/fleet-type.helper';
import { FleetService } from '../../dashboard/fleet/common/services/fleet.service';
import { IntegrationProvider } from '../../dashboard/management-panel/integrations/common/enums/integration-provider';
import { OrderPointStatus } from '../../dashboard/orders/common/enums/order-point-status';
import { OrderProposalStatus } from '../../dashboard/orders/common/enums/order-proposal-status';
import { OrderProposalType } from '../../dashboard/orders/common/enums/order-proposal-type';
import { OrderStatus } from '../../dashboard/orders/common/enums/order-status';
import { OrderHelper } from '../../dashboard/orders/common/helpers/order.helper';
import type { OrderPointTimestamps } from '../../dashboard/orders/common/interfaces/order-point';
import type { OrderPointDriverReports } from '../../dashboard/orders/common/interfaces/order-point-driver-reports';
import { OrdersService } from '../../dashboard/orders/common/services/orders.service';
import { FERRIES } from '../constants/transit/ferries';
import { Country } from '../enums/country';
import { TachographWeeklyRestType } from '../enums/tachograph-weekly-rest-type';
import { TachographWorkingMode } from '../enums/tachograph-working-mode';
import { TelematicsEventType } from '../enums/telematics/telematics-event-type';
import { TelematicsItemFactory } from '../factories/telematics/telematics-item.factory';
import { TelematicsManualUpdateType } from '../factories/telematics/telematics-manual-update-type';
import { CommonHelper } from '../helpers/common.helper';
import { RouteHelper } from '../helpers/route.helper';
import { SseHelper } from '../helpers/sse.helper';
import type {
  Tachograph,
  TachographTimeCounter,
} from '../interfaces/common/tachograph';
import type {
  TelematicsEvent,
  TelematicsFleetData,
  TelematicsOrderStatusData,
  TelematicsRouteData,
  TelematicsTachographData,
  TelematicsTelemetryData,
} from '../interfaces/telematics/telematics-event';
import type { TelematicsInitialData } from '../interfaces/telematics/telematics-initial-data';
import type { TelematicsItem } from '../interfaces/telematics/telematics-item';
import type {
  TelematicsManualOrderPointStatusUpdateData,
  TelematicsManualTechnicalPointStatusUpdateData,
  TelematicsManualUpdateEvent,
} from '../interfaces/telematics/telematics-manual-update-event';
import type {
  TelematicsOrderRoutePointDto,
  TelematicsRoutePointDto,
} from '../interfaces/telematics/telematics-route-point';
import { TelematicsRoutePointStatus } from '../interfaces/telematics/telematics-vehicle-point-status';
import { ApiService } from './api.service';

@Service()
export class TelematicsDataService extends ApiService {
  private readonly authStore = inject(AuthStore);
  private readonly fleetService = inject(FleetService);
  private readonly ordersService = inject(OrdersService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly commonHelper = inject(CommonHelper);
  private readonly sseHelper = inject(SseHelper);

  private data$?: Observable<Map<string, TelematicsItem> | null>;
  private manualUpdate$ = new Subject<TelematicsManualUpdateEvent>();

  public getData$(): Observable<Map<string, TelematicsItem> | null> {
    if (this.data$) return this.data$;

    const fromInitialData = (initialData: TelematicsInitialData) => ({
      loading: ENABLE_MOCKS,
      fleet: new Map(initialData.fleet.map((data) => [data.uuid, data])),
      telemetries: new Map(
        initialData.telemetries.map((data) => [data.fleetUuid, data]),
      ),
      routes: new Map(initialData.routes.map((data) => [data.fleetUuid, data])),
      tachographs: new Map<string, Tachograph>(),
    });

    interface TelematicsDataState {
      initialData: ReturnType<typeof fromInitialData> | null;
      pendingEvents: (TelematicsEvent | TelematicsManualUpdateEvent)[];
    }

    const applyEvent = (
      data: ReturnType<typeof fromInitialData>,
      event: TelematicsEvent | TelematicsManualUpdateEvent,
    ) => {
      if (ENABLE_MOCKS) data.loading = false;

      const { type: eventType, data: eventData } = event;
      switch (eventType) {
        case TelematicsEventType.FLEET:
          data.fleet.set(eventData.uuid, eventData);
          break;

        case TelematicsEventType.TELEMETRY:
          data.telemetries.set(eventData.fleetUuid, eventData);
          break;

        case TelematicsEventType.ROUTE:
          data.routes.set(eventData.fleetUuid, eventData);
          break;

        case TelematicsEventType.TACHOGRAPH:
          data.tachographs.set(eventData.driverUuid, eventData.tachograph);
          break;

        case TelematicsManualUpdateType.DELEGATION: {
          const fleet = data.fleet.get(eventData.fleetUuid);
          if (!fleet) break;

          const { delegations } = fleet;
          const index = delegations.findIndex(
            ({ uuid }) => uuid === eventData.delegation.uuid,
          );

          const updatedDelegations =
            index === -1
              ? [...delegations, eventData.delegation]
              : delegations.with(index, eventData.delegation);

          data.fleet.set(eventData.fleetUuid, {
            ...fleet,
            delegations: updatedDelegations,
          });
          break;
        }

        case TelematicsEventType.ORDER_POINT_STATUS:
        case TelematicsManualUpdateType.ORDER_POINT_STATUS: {
          const {
            fleetUuid,
            orderPointUuid,
            status,
            timestamps: eventTimestamps,
            driverReports: eventReports,
          } = eventData;
          const route = data.routes.get(fleetUuid);
          if (!route?.route) break;

          data.routes.set(fleetUuid, {
            ...route,
            route: this.getRouteAfterPointStatusUpdate(
              'point',
              route.route,
              orderPointUuid,
              status,
              eventTimestamps,
              eventReports,
            ),
          });
          break;
        }

        case TelematicsEventType.ORDER_STATUS:
        case TelematicsManualUpdateType.ORDER_STATUS: {
          const route = data.routes.get(eventData.fleetUuid);
          if (!route?.route) break;

          data.routes.set(eventData.fleetUuid, {
            ...route,
            route: route.route.map(
              (routePoint) =>
                ({
                  ...routePoint,
                  ...(routePoint.order?.uuid === eventData.orderUuid &&
                    routePoint.point && {
                      order: {
                        ...routePoint.order,
                        status: eventData.status,
                      },
                    }),
                }) as TelematicsRoutePointDto,
            ),
          });
          break;
        }

        case TelematicsManualUpdateType.TECHNICAL_POINT_STATUS: {
          const {
            fleetUuid,
            technicalPointUuid,
            status,
            timestamps: eventTimestamps,
            driverReports: eventReports,
          } = eventData;
          const route = data.routes.get(fleetUuid);
          if (!route?.route) break;

          data.routes.set(fleetUuid, {
            ...route,
            route: this.getRouteAfterPointStatusUpdate(
              'technicalPoint',
              route.route,
              technicalPointUuid,
              status,
              eventTimestamps,
              eventReports,
            ),
          });
          break;
        }
      }

      return data;
    };

    const initialData$ = this.getInitialData$().pipe(
      map((initialData) => ({ initialData: fromInitialData(initialData) })),
    );
    const events$ = merge(this.getUpdates$(), this.manualUpdate$).pipe(
      map((event) => ({ event })),
    );

    this.data$ = merge(initialData$, events$).pipe(
      scan(
        (
          { initialData, pendingEvents }: TelematicsDataState,
          update,
        ): TelematicsDataState => {
          if ('initialData' in update) {
            return {
              initialData: pendingEvents.reduce(applyEvent, update.initialData),
              pendingEvents: [],
            };
          } else if (!initialData) {
            return {
              initialData,
              pendingEvents: [...pendingEvents, update.event],
            };
          }

          return {
            initialData: applyEvent(initialData, update.event),
            pendingEvents,
          };
        },
        { initialData: null, pendingEvents: [] },
      ),
      map(({ initialData }) => initialData),
      filter((initialData) => initialData !== null),
      combineLatestWith(this.commonHelper.nowMinute$),
      scan((previous: Map<string, TelematicsItem> | null, [data, now]) => {
        if (data.loading) return null;

        return new Map(
          [...data.fleet.values()]
            .map((fleet) => ({
              fleet,
              telemetry: data.telemetries.get(fleet.uuid),
              route: data.routes.get(fleet.uuid),
            }))
            .filter(({ fleet }) => !fleet.assignedVehicle)
            .filter((events) => events.route)
            .map((events) =>
              TelematicsItemFactory.from({
                fleet: events.fleet,
                telemetry: events.telemetry ?? null,
                route: events.route!,
                tachographs: data.tachographs,
                now,
                previous: previous?.get(events.fleet.uuid) ?? null,
                injector: this.injector,
              }),
            )
            .map((item) => [item.uuid, item]),
        );
      }, null),
      shareReplay(1),
      takeUntil(this.authStore.logout$),
      takeUntilDestroyed(this.destroyRef),
    );

    this.authStore.logout$.subscribe(() => (this.data$ = undefined));

    return this.data$;
  }

  public updateDelegation(
    fleetUuid: string,
    delegation: SimplifiedDelegation,
  ): void {
    this.manualUpdate$.next({
      type: TelematicsManualUpdateType.DELEGATION,
      data: {
        fleetUuid,
        delegation,
      },
    });
  }

  public updateOrderPointStatus(
    data: TelematicsManualOrderPointStatusUpdateData,
  ): void {
    this.manualUpdate$.next({
      type: TelematicsManualUpdateType.ORDER_POINT_STATUS,
      data,
    });
  }

  public updateOrderStatus(data: TelematicsOrderStatusData): void {
    this.manualUpdate$.next({
      type: TelematicsManualUpdateType.ORDER_STATUS,
      data,
    });
  }

  public updateTechnicalPointStatus(
    data: TelematicsManualTechnicalPointStatusUpdateData,
  ): void {
    this.manualUpdate$.next({
      type: TelematicsManualUpdateType.TECHNICAL_POINT_STATUS,
      data,
    });
  }

  private getRouteAfterPointStatusUpdate(
    pointKey: 'point' | 'technicalPoint',
    route: TelematicsRoutePointDto[],
    pointUuid: string,
    status: OrderPointStatus,
    eventTimestamps: OrderPointTimestamps,
    eventReports: OrderPointDriverReports | undefined,
  ): TelematicsRoutePointDto[] {
    return route.map((routePoint): TelematicsRoutePointDto => {
      if (RouteHelper.getUuid(routePoint) !== pointUuid) return routePoint;

      const routePointStatus =
        OrderHelper.getRoutePointStatusByOrderPointStatus(
          status,
          routePoint.order,
        );
      const existingTimestamps = RouteHelper.getTimestamps(routePoint);
      const { arrivedAt, startedAt, completedAt, canceledAt } = eventTimestamps;
      const timestamps: OrderPointTimestamps = {
        arrivedAt,
        startedAt,
        completedAt,
        canceledAt: canceledAt ?? existingTimestamps?.canceledAt ?? null,
      };

      const point = routePoint[pointKey];
      const driverReports = eventReports ?? point?.driverReports;

      return {
        ...routePoint,
        status: routePointStatus,
        [pointKey]: {
          ...point,
          status,
          timestamps,
          ...(driverReports && { driverReports }),
        },
      };
    });
  }

  private getInitialData$(): Observable<TelematicsInitialData> {
    if (!ENABLE_MOCKS) return this.get('/telematics/initial/data');

    return of({ fleet: [], telemetries: [], routes: [] });
  }

  private getUpdates$(): Observable<TelematicsEvent> {
    if (!ENABLE_MOCKS) {
      return this.sseHelper
        .connect$(
          () => this.getMercureUrl(),
          () => this.authStore.token()!,
        )
        .pipe(
          map((data) => JSON.parse(data) as TelematicsEvent),
          takeUntil(this.authStore.logout$),
          takeUntilDestroyed(this.destroyRef),
        );
    }

    const updates$ = new Subject<TelematicsEvent>();
    forkJoin([
      this.fleetService
        .listFleet({
          filters: [FleetHelper.createStateFilter(FleetState.ACTIVE)],
          page: 1,
          limit: Number.MAX_SAFE_INTEGER,
        })
        .pipe(map((response) => response.items)),
      this.ordersService
        .listOrders({
          page: 1,
          limit: Number.MAX_SAFE_INTEGER,
        })
        .pipe(map((response) => response.items)),
    ])
      .pipe(
        map(([fleet, orders]): TelematicsEvent[] => {
          const fleetEvents = fleet
            .filter(
              (fleet) =>
                FleetTypeHelper.isVehicle(fleet) ||
                (!fleet.vehicle && Math.random() < 0.25),
            )
            .map((fleet): TelematicsFleetData => ({
              uuid: fleet.uuid,
              status: fleet.status,
              generalInformation: fleet.generalInformation,
              vehicleMeasurements: fleet.vehicleMeasurements,
              cargoSpace: fleet.cargoSpace,
              fuelTankCapacity: fleet.fuelTankCapacity,
              parameters: fleet.additionalParameters,
              allowedCountries: fleet.allowedCountries,
              co2Emissions: fleet.co2Emissions ?? undefined,
              assignedTrailer: fleet.trailer ?? null,
              assignedVehicle: null,
              assignedEmployees: fleet.assignedEmployees ?? [],
              drivers: mapValues(fleet.drivers, (driver) =>
                driver
                  ? {
                      uuid: driver.uuid,
                      driversLicenseAndProfessionalQualifications:
                        driver.driversLicenseAndProfessionalQualifications,
                      driverData: driver.driverData,
                      delegation: fleet.delegation,
                      unavailability: driver.unavailability,
                      tachograph: null,
                      telematics: null,
                    }
                  : null,
              ),
              delegations: fleet.delegation
                ? [fleet.delegation]
                : Math.random() < 0.5
                  ? [
                      {
                        uuid: uuidv7(),
                        identifier: 'MOCK',
                        status: {
                          value: DelegationStatus.PLANNED,
                          color: 'blue' as const,
                        },
                        start: {
                          address: sample(Object.values(FERRIES))!.address,
                          date: DateTime.now()
                            .plus({ days: random(-1, 2) })
                            .toISODate(),
                          time: Duration.fromObject({
                            hour: random(0, 23),
                            minutes: random(0, 59),
                          }).toISOTime({
                            suppressMilliseconds: true,
                          }) as OnyxTime,
                          timezone: 'Europe/Warsaw',
                        },
                        primaryDriver: {
                          uuid: uuidv7(),
                          driverCard: {
                            expirationDate: DateTime.now()
                              .plus({ days: random(3, 5) })
                              .toISODate(),
                            issuingCountry: Country.PL,
                            number: '123456789',
                            scans: [],
                          },
                          driversLicenseAndProfessionalQualifications: {
                            categories: [
                              {
                                category: 'C',
                                expirationDate: {
                                  date: DateTime.now()
                                    .plus({ days: random(3, 5) })
                                    .toISODate(),
                                },
                              },
                            ],
                            driversLicenseNumber: 'ABC123456',
                            driversLicenseIssuingCountry: Country.PL,
                            professionalQualificationsExpirationDate: null,
                            scans: [],
                          },
                          driverData: {
                            assignedBase: null,
                            birthDate: null,
                            firstName: 'John',
                            lastName: 'Doe',
                            homeAddress: {
                              apartmentNumber: null,
                              city: 'Warsaw',
                              coordinates: {
                                latitude: 52.2297,
                                longitude: 21.0122,
                              },
                              countryCode: 'pl',
                              houseNumber: '1',
                              label: 'ul. Mockowa 1, 00-001 Warsaw, Poland',
                              name: 'ul. Mockowa 1',
                              street: 'ul. Mockowa',
                              type: OnyxAddressType.CUSTOM,
                              zipCode: '00-001',
                            },
                            nationality: Country.PL,
                            peselNumber: null,
                            privateEmail: null,
                            privatePhone: {
                              areaCode: '48',
                              number: '123456789',
                            },
                            workPhone: null,
                          },
                          tachograph: null,
                          telematics: null,
                          unavailability: null,
                        },
                        secondaryDriver: null,
                        end: {
                          address:
                            Math.random() < 0.5
                              ? sample(Object.values(FERRIES))!.address
                              : null,
                          date:
                            Math.random() < 0.5
                              ? DateTime.now()
                                  .plus({ days: random(3, 5) })
                                  .toISODate()
                              : null,
                          time:
                            Math.random() < 0.5
                              ? (Duration.fromObject({
                                  hour: random(0, 23),
                                  minutes: random(0, 59),
                                }).toISOTime({
                                  suppressMilliseconds: true,
                                }) as OnyxTime)
                              : null,
                          timezone: 'Europe/Warsaw',
                        },
                        isReturning: Math.random() < 0.5,
                        relatedFleetSwap: null,
                      },
                    ]
                  : [],
              telematics: fleet.telematics,
            }))
            .map((data) => ({
              type: TelematicsEventType.FLEET as const,
              data,
            }));

          const routeEvents = fleet
            .map((fleet): TelematicsRouteData => {
              if (!FleetTypeHelper.isVehicle(fleet)) {
                return {
                  fleetUuid: fleet.uuid,
                  route: [],
                };
              }

              const routeOrders = sampleSize(
                orders,
                Math.round(random(0.25, Math.min(5, orders.length))),
              )!;
              const hasToAccept = Math.random() < 0.3;

              return {
                fleetUuid: fleet.uuid,
                route: chain(routeOrders)
                  .flatMap((order): TelematicsOrderRoutePointDto[] => {
                    const isToAccept = hasToAccept && Math.random() < 0.5;
                    const orderData: TelematicsOrderRoutePointDto['order'] = {
                      uuid: order.uuid,
                      identifier: order.identifier,
                      author: order.author,
                      status: {
                        ...order.status,
                        value: isToAccept
                          ? OrderStatus.TO_ACCEPT
                          : OrderStatus.IN_PROGRESS,
                      },
                      proposalData: isToAccept
                        ? {
                            uuid: uuidv7(),
                            type: OrderProposalType.ENGINE,
                            status: OrderProposalStatus.CREATED,
                            vehicle: fleet,
                            trailer: fleet.trailer!,
                            primaryDriver: fleet.drivers!.primaryDriver!,
                            secondaryDriver: fleet.drivers!.secondaryDriver,
                            result: {
                              emptyDistance: 0,
                              emptyDistanceDuration: 0,
                              price: order.price,
                            },
                            canAccept: true,
                            canReject: true,
                            creation: {
                              author: null,
                              createdAt: DateTime.utc().toISO(),
                            },
                            acceptance: null,
                            rejection: null,
                            cancelation: null,
                          }
                        : null,
                      basicInformation: order.basicInformation,
                      sender: order.basicInformation.sender,
                      isDedicated: order.parameters.isDedicated,
                      sentNotificationNumber:
                        order.parameters.sentNotificationNumber,
                      pointsCount: order.points.length,
                      route: order.route,
                    };

                    return order.points.map((point) => ({
                      primaryDriverDelegationUuid:
                        fleet.delegation?.uuid ?? uuidv7(),
                      secondaryDriverDelegationUuid: fleet.drivers
                        ?.secondaryDriver
                        ? (fleet.delegation?.uuid ?? uuidv7())
                        : null,
                      order: orderData,
                      point,
                      route: point.route,
                      technicalPoint: null,
                      trailerUuid: fleet.trailer?.uuid ?? null,
                      status: TelematicsRoutePointStatus.PLANNED,
                      deadlineTime: DateTime.now().plus({
                        minutes: random(1, 180),
                      }),
                      bufferTime: Duration.fromObject({
                        minutes: random(-120, 120),
                      }),
                    }));
                  })
                  .thru((points) => {
                    const hasCompleted = Math.random() < 0.5;
                    const hasInProgress = Math.random() < 0.5;
                    const currentIndex = hasCompleted
                      ? random(1, points.length - 1)
                      : 0;

                    return points.map((point, index) => {
                      const isToAccept =
                        point.order.status.value === OrderStatus.TO_ACCEPT;
                      const beforeToAccept = points
                        .slice(0, index)
                        .every(
                          (point) =>
                            point.order.status.value !== OrderStatus.TO_ACCEPT,
                        );
                      const isCompleted =
                        beforeToAccept && hasCompleted && index < currentIndex;
                      const isInProgress =
                        beforeToAccept &&
                        hasInProgress &&
                        index === currentIndex;

                      const arrival = DateTime.now().plus({
                        hours:
                          (index - currentIndex) * 8 +
                          (isCompleted || isInProgress ? -3 : 2),
                      });
                      const departure = arrival.plus({ hours: 1 });
                      const arrivalDeadline = DateTime.now().plus({
                        hours:
                          (index - currentIndex) * 8 +
                          (isCompleted || isInProgress ? -2 : 3),
                      });
                      const departureDeadline = arrivalDeadline.plus({
                        hours: 1,
                      });

                      return {
                        ...point,
                        point: {
                          ...point.point,
                          uuid: uuidv7(),
                          estimations: {
                            arrivalTime: arrival.toISO(),
                            departureTime: departure.toISO(),
                            arrivalDeadlineTime: arrivalDeadline.toISO(),
                            departureDeadlineTime: departureDeadline.toISO(),
                            remainingDistance: isInProgress
                              ? random(0, point.route.summary.distance)
                              : null,
                          },
                          timestamps: {
                            arrivedAt:
                              isCompleted || isInProgress
                                ? arrival.toISO()
                                : null,
                            startedAt:
                              isCompleted || isInProgress
                                ? arrival.toISO()
                                : null,
                            completedAt: isCompleted ? departure.toISO() : null,
                            canceledAt: null,
                          },
                          status: isToAccept
                            ? OrderPointStatus.TO_DO
                            : isCompleted
                              ? OrderPointStatus.COMPLETED
                              : isInProgress
                                ? OrderPointStatus.IN_PROGRESS
                                : OrderPointStatus.TO_DO,
                        },
                      };
                    });
                  })
                  .thru((routePoints) =>
                    routePoints.map((routePoint) => {
                      const orderUuid = routePoint.order.uuid;
                      const isOrderFinished = routePoints
                        .filter(({ order }) => order.uuid === orderUuid)
                        .every(
                          ({ point }) =>
                            point.status === OrderPointStatus.COMPLETED,
                        );
                      if (isOrderFinished) {
                        routePoint.order.status.value =
                          OrderStatus.COMPLETING_DOCUMENTS;
                      }

                      return routePoint;
                    }),
                  )
                  .value(),
              };
            })
            .map((data) => ({
              type: TelematicsEventType.ROUTE as const,
              data,
            }));

          const telemetryEvents = fleetEvents
            .map(({ data: fleetData }): TelematicsTelemetryData => ({
              fleetUuid: fleetData.uuid,
              telemetry: {
                adBlueLevel: random(10, 50),
                adBlueMaxLevel: 50,
                adBluePercentageLevel: random(10, 100),
                altitude: random(0, 2000),
                averageFuelConsumption: random(25, 38),
                axleWeight: random(7000, 12000),
                azimuth: random(0, 360),
                coordinates: routeEvents
                  .find(
                    ({ data: routeData }) =>
                      routeData.fleetUuid === fleetData.uuid,
                  )
                  ?.data.route?.at(0)?.route?.departure ?? {
                  latitude: random(47.8, 54.0, true),
                  longitude: random(7.0, 20.0, true),
                },
                diagnosticCodes: null,
                fuelLevel: fleetData.fuelTankCapacity?.totalCapacity
                  ? random(0, fleetData.fuelTankCapacity.totalCapacity)
                  : null,
                fuelMaxLevel: random(600, 1200),
                fuelPercentageLevel: random(5, 100),
                isEngineOn: random(0, 1) === 1,
                isPtoActive: random(0, 1) === 1,
                serviceDistance: random(0, 30000),
                speed: random(0, 90),
                totalDistance: random(100000, 900000),
                totalFuelConsumption: random(50000, 300000),
                updatedAt: new Date().toISOString(),
                weight: random(15000, 40000),
              },
              telematics: fleetData.telematics ?? {
                provider: sample(Object.values(IntegrationProvider))!,
                uuid: uuidv7(),
                deviceId: fleetData.uuid,
              },
            }))
            .map((data) => ({
              type: TelematicsEventType.TELEMETRY as const,
              data,
            }));

          const tachographEvents = chain(fleetEvents)
            .map(({ data: fleetData }) => Object.values(fleetData.drivers))
            .flatten()
            .compact()
            .map((driver): TelematicsTachographData => {
              const CONTINUOUS_DRIVING_LIMIT = Duration.fromObject({
                hours: 4,
                minutes: 30,
              }).as('seconds');
              const MAX_DAILY_DRIVING_LIMIT = Duration.fromObject({
                hours: 10,
              }).as('seconds');
              const WEEKLY_DRIVING_LIMIT = Duration.fromObject({
                hours: 56,
              }).as('seconds');
              const FORTNIGHT_DRIVING_LIMIT = Duration.fromObject({
                hours: 90,
              }).as('seconds');
              const MAX_DAILY_AMPLITUDE_LIMIT = Duration.fromObject({
                hours: 15,
              }).as('seconds');
              const WORK_WEEK_LIMIT = Duration.fromObject({
                hours: 144,
              }).as('seconds');

              const elapsedContinuousDriving = random(
                0,
                CONTINUOUS_DRIVING_LIMIT,
              );
              const elapsedDailyDriving = random(
                elapsedContinuousDriving,
                MAX_DAILY_DRIVING_LIMIT,
              );
              const elapsedWeeklyDriving = random(
                elapsedDailyDriving,
                WEEKLY_DRIVING_LIMIT,
              );
              const elapsedFortnightDriving = random(
                elapsedWeeklyDriving,
                FORTNIGHT_DRIVING_LIMIT,
              );
              const elapsedDailyAmplitude = random(
                elapsedDailyDriving,
                MAX_DAILY_AMPLITUDE_LIMIT,
              );
              const elapsedWorkWeek = random(
                elapsedWeeklyDriving,
                WORK_WEEK_LIMIT,
              );

              const getTimeCounter = (
                elapsed: number,
                limit: number,
              ): TachographTimeCounter => ({
                elapsed,
                remaining: limit - elapsed,
                effectiveElapsed: elapsed,
                effectiveRemaining: limit - elapsed,
                limit,
              });

              const getExtensionCounter = (used: number, limit: number) => ({
                used,
                remaining: limit - used,
                limit,
              });

              return {
                driverUuid: driver.uuid,
                telematics: {
                  provider: sample(Object.values(IntegrationProvider))!,
                  uuid: uuidv7(),
                  driverId:
                    driver.telematics?.driverId ?? driver.driverData.firstName,
                },
                tachograph: {
                  workingMode: sample(Object.values(TachographWorkingMode))!,
                  workingModeStartedAt: DateTime.now()
                    .minus({ minutes: random(30, 200) })
                    .toISO(),
                  continuousDriving: getTimeCounter(
                    elapsedContinuousDriving,
                    CONTINUOUS_DRIVING_LIMIT,
                  ),
                  dailyDriving: getTimeCounter(
                    elapsedDailyDriving,
                    MAX_DAILY_DRIVING_LIMIT,
                  ),
                  weeklyDriving: getTimeCounter(
                    elapsedWeeklyDriving,
                    WEEKLY_DRIVING_LIMIT,
                  ),
                  fortnightDriving: getTimeCounter(
                    elapsedFortnightDriving,
                    FORTNIGHT_DRIVING_LIMIT,
                  ),
                  dailyAmplitude: getTimeCounter(
                    elapsedDailyAmplitude,
                    MAX_DAILY_AMPLITUDE_LIMIT,
                  ),
                  workWeek: getTimeCounter(elapsedWorkWeek, WORK_WEEK_LIMIT),
                  dailyDrivingTimeExtensions: getExtensionCounter(
                    random(0, 2),
                    2,
                  ),
                  reducedDailyRestExtensions: getExtensionCounter(
                    random(0, 3),
                    3,
                  ),
                  estimatedBreakEndAt: null,
                  nextEstimatedDailyRestAt: DateTime.now()
                    .plus({ day: 1 })
                    .toISO(),
                  nextEstimatedWeeklyRestAt: DateTime.now()
                    .plus({ days: 7 })
                    .toISO(),
                  lastWeeklyRestType: sample(
                    Object.values(TachographWeeklyRestType),
                  )!,
                  updatedAt: DateTime.now()
                    .minus({ minutes: random(30, 90) })
                    .toISO(),
                },
              };
            })
            .map((data) => ({
              type: TelematicsEventType.TACHOGRAPH as const,
              data,
            }))
            .value();

          return [
            ...fleetEvents,
            ...telemetryEvents,
            ...routeEvents,
            ...tachographEvents,
          ];
        }),
        tap((events) => {
          // eslint-disable-next-line no-console
          console.log('Mocked telematics events:', events);
          // eslint-disable-next-line no-console
          console.log(
            'Mocked telematics events (JSON):',
            JSON.stringify(events),
          );

          for (const event of events) updates$.next(event);
        }),
        map((event) =>
          event.filter(
            (message) => message.type === TelematicsEventType.TELEMETRY,
          ),
        ),
        switchMap((events) =>
          this.commonHelper.nowMinute$.pipe(map(() => events)),
        ),
      )
      .subscribe((events) => {
        for (const event of events) {
          const { telemetry } = event.data;
          const {
            coordinates,
            fuelLevel,
            adBlueLevel,
            totalDistance,
            totalFuelConsumption,
            weight,
            averageFuelConsumption,
            azimuth,
            speed,
          } = telemetry;

          event.data.telemetry = {
            adBlueLevel:
              adBlueLevel != null
                ? Math.max(adBlueLevel - random(0, 0.2, true), 0)
                : null,
            adBlueMaxLevel: 50,
            adBluePercentageLevel:
              adBlueLevel != null ? Math.round((adBlueLevel / 50) * 100) : null,
            altitude: random(0, 2000),
            averageFuelConsumption:
              averageFuelConsumption != null
                ? clamp(
                    averageFuelConsumption + random(-0.5, 0.5, true),
                    25,
                    38,
                  )
                : null,
            axleWeight:
              weight != null
                ? Math.floor(weight / 4 + random(-500, 500))
                : null,
            azimuth: (azimuth + random(-5, 15)) % 360,
            coordinates: {
              latitude: coordinates.latitude + random(-0.01, 0.01, true),
              longitude: coordinates.longitude + random(-0.01, 0.01, true),
            },
            diagnosticCodes: null,
            fuelLevel:
              fuelLevel != null
                ? fuelLevel === 0
                  ? random(100, 500)
                  : Math.max(fuelLevel - random(0, 5), 0)
                : null,
            fuelMaxLevel: 1000,
            fuelPercentageLevel:
              fuelLevel != null ? Math.round((fuelLevel / 1000) * 100) : null,
            isEngineOn: speed != null ? speed > 0 : telemetry.isEngineOn,
            isPtoActive: telemetry.isPtoActive,
            serviceDistance: clamp(random(0, 30000) - random(10, 50), 0, 30000),
            speed: speed != null ? clamp(speed + random(-3, 5), 0, 90) : null,
            totalDistance:
              totalDistance != null && speed != null
                ? totalDistance + random(0.05, 1.0, true)
                : totalDistance,
            totalFuelConsumption:
              totalFuelConsumption != null
                ? totalFuelConsumption + random(0.1, 0.5, true)
                : null,
            updatedAt: DateTime.utc()
              .minus({ minutes: random(0, 100) })
              .toISO(),
            weight:
              weight != null
                ? clamp(weight + random(-500, 500), 15000, 40000)
                : null,
          };

          updates$.next(event);
        }
      });

    return updates$.asObservable();
  }

  private getMercureUrl(): string {
    const url = getNamespacedUrl(
      environment.mercureUrl,
      this.authStore.companyId(),
    );
    const businessUuid = this.authStore.business()!.uuid;

    return `${url}?match=/api/telematics/${businessUuid}`;
  }
}
